import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { Dropzone, FileItem } from "@dropzone-ui/react";
import axios from "axios";
import { apiUrl } from "../../constant";
import { authtoken } from "../../global";
import { toast } from "react-toastify";

const MAX_FILE_SIZE = 104857600; // 100MB in bytes

const UploadDocumentsModal = ({ isOpen, onClose, onSuccess, jobId }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFilesChange = (incomingFiles) => {
    const validFiles = [];
    let oversized = false;
    incomingFiles.forEach((file) => {
      if (file.file && file.file.size > MAX_FILE_SIZE) {
        oversized = true;
      } else {
        validFiles.push(file);
      }
    });
    if (oversized) {
      toast.error("File size should not exceed 100MB");
    }
    setFiles(validFiles);
  };

  const uploadFiles = async () => {
    if (!files.length) {
      toast.warning("Please select files");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("task_id", jobId);

    files.forEach((file) => {
      formData.append("file[]", file.file);
    });

    // Only the upload request itself is guarded by this catch. Post-upload UI
    // side-effects (refresh, close) run afterwards so an error in them can't be
    // misreported as an "Upload failed" toast.
    let imported = null;
    try {
      const res = await axios.post(`${apiUrl}/upload-document1`, formData, {
        headers: {
          Authorization: `Bearer ${authtoken}`,
        },
        timeout: 10 * 60 * 1000, // 10 minutes for large uploads
      });
      imported = res?.data?.imported ?? null;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (err?.code === "ECONNABORTED"
          ? "Upload timed out. The file may still be processing."
          : "Upload failed");
      toast.error(msg);
      setUploading(false);
      return;
    }

    setUploading(false);
    // When a .zip/.eml/.msg is expanded, more documents come out than were
    // selected — surface the real imported count.
    toast.success(
      imported && imported > files.length
        ? `Imported ${imported} documents`
        : "Documents uploaded successfully"
    );
    setFiles([]);
    if (onSuccess) onSuccess();
    else onClose();
  };

  return (
    <Modal isOpen={isOpen} size="lg" backdrop="static">
      <ModalBody>
        <h3>Upload Documents</h3>

        <Dropzone
          value={files}
          onChange={handleFilesChange}
          maxFileSize={MAX_FILE_SIZE}
          multiple
          footer={false}
          header={false}
        />

        <p className="text-muted mb-0 mt-2" style={{ fontSize: "12px" }}>
          You can also upload a <strong>.zip</strong>, <strong>.eml</strong> or{" "}
          <strong>.msg</strong> — its contents (and email attachments) are
          extracted and imported automatically.
        </p>

        {files.map((file) => (
          <FileItem
            {...file}
            key={file.id}
            onDelete={() =>
              setFiles((prev) => prev.filter((f) => f.id !== file.id))
            }
            preview
          />
        ))}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          onClick={uploadFiles}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>

        <Button color="secondary" onClick={onClose}>
          Skip
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UploadDocumentsModal;
